import React, { memo, useCallback } from 'react';
import {
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SortablePhoneList, SortableEmailList } from './SortablePhoneEmailList';
import { CONTACT_TYPES, EMPTY_ADDRESS } from '../utils/enums';
import { FormSection, FormRow } from '../../../components/common/form';

const ContactForm = memo(function ContactForm({
  formData,
  onInputChange,
  validationErrors = {},
  loading = false,
}) {
  const { t } = useTranslation(['contacts', 'common']);

  const handleChange = useCallback((field, value) => {
    onInputChange(field, value);
  }, [onInputChange]);

  const handleAddressChange = useCallback((field, value) => {
    const current = formData.address || EMPTY_ADDRESS;
    onInputChange('address', { ...current, [field]: value });
  }, [formData.address, onInputChange]);

  const hasAddress = Boolean(formData.address);

  const toggleAddress = useCallback(() => {
    onInputChange('address', hasAddress ? null : { ...EMPTY_ADDRESS });
  }, [hasAddress, onInputChange]);

  return (
    <>
      <FormSection title={t('contacts:sections.basic_info')}>
        <FormRow widths={[2, 1]}>
          <TextField
            fullWidth
            required
            label={t('contacts:fields.name')}
            value={formData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!validationErrors.name}
            helperText={validationErrors.name}
            disabled={loading}
          />
          <TextField
            fullWidth
            select
            required
            label={t('contacts:fields.contact_type')}
            value={formData.contact_type || 'company'}
            onChange={(e) => handleChange('contact_type', e.target.value)}
            disabled={loading}
          >
            {CONTACT_TYPES.map((ct) => (
              <MenuItem key={ct.value} value={ct.value}>
                {t(ct.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        </FormRow>
        <FormRow>
          <TextField
            fullWidth
            label={t('contacts:fields.att')}
            value={formData.att || ''}
            onChange={(e) => handleChange('att', e.target.value)}
            disabled={loading}
            placeholder={t('contacts:placeholders.att')}
          />
          <TextField
            fullWidth
            label={t('contacts:fields.role')}
            value={formData.role || ''}
            onChange={(e) => handleChange('role', e.target.value)}
            disabled={loading}
            placeholder={t('contacts:placeholders.role')}
          />
        </FormRow>
      </FormSection>

      <FormSection>
        <SortablePhoneList
          phones={formData.phone_numbers || []}
          onChange={(phones) => handleChange('phone_numbers', phones)}
          disabled={loading}
        />
        <SortableEmailList
          emails={formData.emails || []}
          onChange={(emails) => handleChange('emails', emails)}
          disabled={loading}
        />
      </FormSection>

      <FormSection
        title={t('contacts:sections.address')}
        action={
          <FormControlLabel
            control={<Switch checked={hasAddress} onChange={toggleAddress} disabled={loading} />}
            label={t('contacts:fields.has_address')}
          />
        }
      >
        {hasAddress && (
          <>
            <TextField
              fullWidth
              label={t('contacts:fields.address_line1')}
              value={formData.address?.address_line1 || ''}
              onChange={(e) => handleAddressChange('address_line1', e.target.value)}
              disabled={loading}
            />
            <TextField
              fullWidth
              label={t('contacts:fields.address_line2')}
              value={formData.address?.address_line2 || ''}
              onChange={(e) => handleAddressChange('address_line2', e.target.value)}
              disabled={loading}
            />
            <FormRow widths={[1, 3]} minWidth={140}>
              <TextField
                fullWidth
                label={t('contacts:fields.postal_code')}
                value={formData.address?.postal_code || ''}
                onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                disabled={loading}
              />
              <TextField
                fullWidth
                label={t('contacts:fields.city')}
                value={formData.address?.city || ''}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                disabled={loading}
              />
            </FormRow>
            <TextField
              fullWidth
              label={t('contacts:fields.country')}
              value={formData.address?.country || ''}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              disabled={loading}
            />
          </>
        )}
      </FormSection>

      <FormSection title={t('contacts:sections.additional_info')}>
        <FormRow>
          <TextField
            fullWidth
            label={t('contacts:fields.website')}
            value={formData.website || ''}
            onChange={(e) => handleChange('website', e.target.value)}
            disabled={loading}
            placeholder="https://"
          />
          <TextField
            fullWidth
            label={t('contacts:fields.cvr_number')}
            value={formData.cvr_number || ''}
            onChange={(e) => handleChange('cvr_number', e.target.value)}
            disabled={loading}
            placeholder="CVR / VAT"
          />
        </FormRow>
      </FormSection>
    </>
  );
});

export default ContactForm;
